import { Box, Flex } from "rebass/styled-components";
import { Swiper } from "swiper/react";
import { Mousewheel, Pagination, Navigation } from "swiper/modules";

import { SectionTitle, StyledSwiperSlide } from "../styledComponents";
import { ICombinedFeedConfig, CombinedFeedViewModel } from "./ViewModel";
import { Loading } from "../Loading";
import { Expandable } from "../../../Components/Expandable";
import { IContentItem } from "../@types";
import { CombinedFeedContentItem } from "./CombinedFeedContentItem";
import { getViewedClips } from "../../../Utilities/ViewedClips";

export interface ICombinedFeedConfiguration {
  title?: string;
  showExpandable?: boolean;
  maxPerRow?: number;

  feedConfig?: ICombinedFeedConfig[];

  shuffleAlgorithm?:
    | "random"
    | "priority"
    | "balanced"
    | "interleaved"
    | "interleavedByPriority"
    | "viewsWeighted";
  totalClipLimit?: number;
  enableReasonBadges?: boolean;
}

interface ICombinedFeedProps extends ICombinedFeedConfiguration {}

const DEFAULT_MAX_PER_ROW = 6;

const mapClipToContentItem = (clip: any): IContentItem => {
  return {
    _id: clip.id,
    thumbnailStandardUrl: clip.thumbnailUrl,
    thumbnailOverlayUrl: clip.thumbnailUrl,
    previewUrl: clip.url || clip.previewUrl,
    createdAt: clip.createdAt,
    clipImageThumb: clip.thumbnailUrl || "",
    clipSnapshot: clip.thumbnailUrl || "",
    clipLink: `/clip/${clip.shareId}`,
    clipPreviewPath: clip.url || "",
    clipTitle: clip.title,
    clipId: clip.id,
    createdDate: clip.createdAt,
    external: false,
    game: parseInt(clip.game) || 0,
    hlsSrc: undefined,
    hasBeenPinned: clip.isPinned,
    shareId: clip.shareId,
    title: clip.title,
    type: "CLIP" as any,
    views: clip.views || 0,
    score: undefined,
    userClipTitle: clip.title,
    user: {
      _id: clip.user?._id || "",
      username: clip.user?.username || "",
      avatar: clip.user?.avatarUrl || "",
    },
  };
};

const CombinedFeed = ({
  title = "Recommended for You",
  showExpandable = true,
  maxPerRow = DEFAULT_MAX_PER_ROW,
  feedConfig,
  shuffleAlgorithm = "balanced",
  totalClipLimit = 15,
  enableReasonBadges = true,
}: ICombinedFeedProps) => {
  const viewedClipIds = getViewedClips();

  const { combinedClips, loading, hasClips } = CombinedFeedViewModel(
    feedConfig,
    shuffleAlgorithm,
    totalClipLimit,
    viewedClipIds,
  );

  if (loading) return <Loading />;
  if (!hasClips) return <></>;

  const firstRowClips = combinedClips.slice(0, maxPerRow);
  const expandableClips = combinedClips.slice(maxPerRow);

  return (
    <Box style={{ position: "relative" }}>
      <Flex alignItems="center" mb={4}>
        <SectionTitle>{title}</SectionTitle>
      </Flex>
      <Box
        mb={6}
        style={{
          position: "relative",
          width: "100%",
        }}
      >
        <CombinedFeedCarousel
          clips={firstRowClips}
          maxPerRow={maxPerRow}
          title={title}
          enableReasonBadges={enableReasonBadges}
        />
      </Box>
      {showExpandable && expandableClips.length > 0 && (
        <Expandable sectionName={title}>
          <Box pt={2}>
            <CombinedFeedCarousel
              clips={expandableClips}
              maxPerRow={maxPerRow}
              title={title}
              enableReasonBadges={enableReasonBadges}
            />
          </Box>
        </Expandable>
      )}
    </Box>
  );
};

interface ICombinedFeedCarouselProps {
  clips: any[];
  maxPerRow: number;
  title: string;
  enableReasonBadges: boolean;
}

const CombinedFeedCarousel = ({
  clips,
  maxPerRow,
  title,
  enableReasonBadges,
}: ICombinedFeedCarouselProps) => {
  return (
    <Swiper
      centeredSlides={false}
      spaceBetween={16}
      slidesPerView={1}
      mousewheel={false}
      breakpoints={{
        640: {
          slidesPerView: 2,
        },
        979: {
          slidesPerView: 3,
        },
        1280: {
          slidesPerView: 4,
        },
        1600: {
          slidesPerView: 5,
        },
        2000: {
          slidesPerView: maxPerRow,
        },
      }}
      modules={[Mousewheel, Pagination, Navigation]}
    >
      {clips.map((clip, index) => (
        <StyledSwiperSlide key={`${clip.id}-${index}`}>
          <CombinedFeedContentItem
            item={mapClipToContentItem(clip)}
            title={title}
            subtextColor="chalk"
            reason={enableReasonBadges ? clip.reason : undefined}
          />
        </StyledSwiperSlide>
      ))}
    </Swiper>
  );
};

export { CombinedFeed };
